import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, onMounted, watch } from "vue";
import { showNotification, showServerError } from "@/utils";
import { CellsType, RawConfig, prepareItems } from "@/utils/CellsFactory";
import { useWindowResize } from "@/utils/useWindowSize";
import router from '@/router';
import BasePageLayout from '@/components-UI/layouts/BasePageLayout.vue';
import BaseTable from '@/components-UI/table/BaseTable.vue';
import BaseButton from '@/components-UI/BaseButton.vue';
import SvgMagicWand from "@/assets/icons/magic-wand.svg";
import TableColumns from "@/components-UI/table/columns/TableColumns.vue";
import EUAccountsFilters from "./EUAccountsFilters.vue";
import BaseDialog from "@/components-UI/modals/BaseDialog.vue";
import { IColumnsGroup, ITableHeader, createColumns } from "@/utils/ColumnsCreator";
import { deleteEUAccount, getEUAccountsList } from "@/api/revenue";
import { IEUAccountList, IEUAccountSingle } from "@/api/types/revenue";
import SvgTrash from "@/assets/icons/trash.svg";
import { storeToRefs } from "pinia";
import { useEUAccountsStore } from "@/store_v3/EUAccountsStore";
import store from "@/store";
import { translateEUAccountStatus, translateEUAccountStatsStatus } from "@/utils/translate";
import BaseStatusV2 from "@/components-UI/BaseStatusV2.vue";

interface IEUAccountListExtraTypes {
    advertiser_id: number,
    advertiser_name: string,
    account_manager: string,
    actions: any,
    stat_status: any,
    stat_id: any,
}

export default /*#__PURE__*/_defineComponent({
  __name: 'EUAccountsList',
  setup(__props) {

const tableLoading = ref(false);
const tableItems = ref<IEUAccountSingle[]>([]);
const total = ref(0);
const offset = ref(0);
const isDeleteDialog = ref(false);
const accountToDelete = ref<IEUAccountSingle | null>(null);
const { updateEUAccountsListFlag } = storeToRefs(useEUAccountsStore());
const { width } = useWindowResize();

const columns = ref<IColumnsGroup[]>();
const headers: ITableHeader[] = [
    { text: 'ID', sortable: false, value: 'id', align: 'center', width: '80px' },
    { text: 'Период', sortable: false, value: 'period', width: '112px', align: 'start' },
    { text: 'ID рекламодателя', sortable: false, value: 'advertiser_id', width: '117px', align: 'center' },
    { text: 'Рекламодатель', sortable: false, value: 'advertiser_name', width: 'auto', align: 'start' },
    { text: 'Автор', sortable: false, value: 'created_by', width: 'auto', align: 'start' },
    { text: 'Аккаунт-менеджер', sortable: false, value: 'account_manager', width: '128px', align: 'start' },
    { text: 'Проект', sortable: false, value: 'project', width: '80px', align: 'start' },
    { text: 'Наша организация', sortable: false, value: 'legal', width: '140px', align: 'start' },
    { text: 'Контрагент', sortable: false, value: 'contractor', width: '140px', align: 'start' },
    { text: 'Статус', sortable: false, value: 'status', width: '140px', align: 'center' },
    { text: 'Статус статистики', sortable: false, value: 'stat_status', width: '140px', align: 'center' },
    { text: 'ID статистики', sortable: false, value: 'stat_id', width: '94px', align: 'center' },
    { text: 'Валюта', sortable: false, value: 'currency', width: '80px', align: 'right' },
    { text: 'Общая сумма услуг', sortable: false, value: 'services_sum', width: '112px', align: 'end' },
    { text: 'Сумма взаимозачёта', sortable: false, value: 'netting_sum', width: '140px', align: 'end' },
    { text: 'Сумма к оплате', sortable: false, value: 'payable_services_sum', width: '112px', align: 'end' },
    { text: 'Дата создания', sortable: false, value: 'created_at', width: '100px', align: 'end' },
    { text: 'Выставлен', sortable: false, value: 'issue_date', width: '100px', align: 'end' },
    { text: 'Дата обновления', sortable: false, value: 'updated_at', width: '114px', align: 'end' },
    { text: '', sortable: false, value: 'actions', fixed: true, align: 'center', width: '48px' },
];

const config: RawConfig<IEUAccountList & IEUAccountListExtraTypes> = {
    [CellsType.Route]: {
        id: {
            name: 'eu-invoice',
            valueKey: 'id',
            isChip: true,
            query: () => ({ tab: 'main' }),
            customParams: (i) => ({ id: String(i.id) }),
        },
        advertiser_id : {
            name: 'advertiser',
            text: (i) => i.advertiser.id,
            customParams: (i) => ({ id: i.advertiser.id }),
            isChip: true,
        },
        advertiser_name: {
            name: 'advertiser',
            valueKey: 'advertiser_name',
            text: (i) => i.advertiser.name,
            customParams: (i) => ({ id: i.advertiser.id }),
        },
        stat_id: {
            name: 'eu-invoice',
            valueKey: 'id',
            isChip: true,
            text: (i) => i.stat_task?.id ?? '',
            query: () => ({ tab: 'main' }),
            customParams: (i) => ({ id: String(i.id) }),
        },
    },
    [CellsType.Status]: {
        status: {
            translate: (s) => translateEUAccountStatus(s as string),
            disableHover: true,
        },
    },
    [CellsType.Text]: {
        account_manager: {
            template: (i) => i.advertiser.account_manager,
        },
    },
    [CellsType.Number]: {
        services_sum: {},
        netting_sum: {},
        payable_services_sum: {},
    },
    [CellsType.Date]: {
        period: {
            format: 'LLLL yyyy',
            isHideTime: true,
        },
        created_at: {
            isHideTime: false,
        },
        issue_date: {
            isHideTime: true,
        },
        updated_at: {
            isHideTime: false,
        },
    },
    [CellsType.Uniq]: {
        actions: {},
        stat_status: {},
    },
};

const getPreparedTableItems = computed(() =>
    prepareItems(
        tableItems.value,
        config,
        headers.map(h => h.value),
    ),
);
const getDefaultColumns = computed(() => headers.map(h => h.value));

const getTitle = computed(() => {
    return width.value <= 768 ? 'Все счета' : 'Счета европейских рекламодателей';
});

const changePage = (offsetValue: number) => {
    offset.value = offsetValue;
    getList();
};

const getList = async () => {
    try {
        tableLoading.value = true;
        const params = { ...store.getters['GET_FILTERS_PARAMS_OBJECT'] };
        params.offset = offset.value;
        params.limit = 100;
        const { data, meta } = await getEUAccountsList(params);
        tableItems.value = data;
        total.value = meta?.total ?? data.length;
    } catch (e) {
        showServerError(e, 'Список европейских счетов не загружен');
    } finally {
        tableLoading.value = false;
    }
};

const goToCreatePage = (): void => {
    router.push({
        name: 'eu-invoice',
        params: {
            id: 'create',
        },
        query: {
            tab: 'main',
        },
    });
};

const openDeleteDialog = (item: IEUAccountSingle): void => {
    item.status = 'deleted',
    accountToDelete.value = item;
    isDeleteDialog.value = true;
};

const handleConfirmDelete = async (): Promise<void> => {
    if (!accountToDelete.value) return;
    const item = accountToDelete.value;
    try {
        isDeleteDialog.value = false;
        accountToDelete.value = null;
        tableLoading.value = true;
        await deleteEUAccount(item.id);
        showNotification(`Счёт ${item.id} удалён`, 'info');
        await getList();
    } catch (e) {
        showServerError(e, 'Удалить не получилось');
    } finally {
        tableLoading.value = false;
    }
};

watch(() => updateEUAccountsListFlag.value, (val) => {
    if (val) {
        offset.value = 0;
        getList();
        updateEUAccountsListFlag.value = false;
    }
});

onMounted(async () => {
    columns.value = createColumns(headers, getDefaultColumns.value);
    await getList();
});

return { __sfc: true,tableLoading, tableItems, total, offset, isDeleteDialog, accountToDelete, updateEUAccountsListFlag, width, columns, headers, config, getPreparedTableItems, getDefaultColumns, getTitle, changePage, getList, goToCreatePage, openDeleteDialog, handleConfirmDelete, BasePageLayout, BaseTable, BaseButton, SvgMagicWand, TableColumns, EUAccountsFilters, BaseDialog, SvgTrash, translateEUAccountStatsStatus, BaseStatusV2 }
}

})